import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { startAuthentication } from '@simplewebauthn/browser';
import { apiClient } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Tenant } from '@/types';
import {
  DEFAULT_FIRM_DELEGATION_SCOPE,
  FIRM_DELEGATION_SCOPES,
  FIRM_DELEGATION_SCOPE_DESCRIPTIONS,
  FIRM_DELEGATION_SCOPE_LABELS,
  type FirmDelegationScope,
} from '@efacture/core/sector';

// ─────────────────────────────────────────────────────────────
// Portail cabinet comptable (P1.4) — accès inter-dossiers + cabinet.
//
// Contrats serveur :
//   GET  /api/auth/accessible-tenants      (authentifié)
//   POST /api/auth/switch-tenant           (authentifié) {tenantId}
//   POST /api/firm/firms                    (authentifié)
//   GET  /api/firm/firms                    (authentifié)
//   GET  /api/firm/firms/:firmId/members    (membre)
//   POST /api/firm/firms/:firmId/members    (EXPERT) {email, role?}
//   DELETE /api/firm/firms/:firmId/members/:userId  (EXPERT)
//   POST /api/firm/firms/:firmId/invitations (EXPERT) {ifu}
//   GET  /api/firm/firms/:firmId/links/:linkId/team          (EXPERT)
//   POST /api/firm/firms/:firmId/links/:linkId/team          (EXPERT) {userId}
//   DELETE /api/firm/firms/:firmId/links/:linkId/team/:userId (EXPERT)
//   GET  /api/firm/links                    (client, tenant:settings)
//   POST /api/firm/links/accept             (client, tenant:settings) {code, scope?}
//   PUT  /api/firm/links/:linkId/scope      (client, tenant:settings) {scope}
//   POST /api/firm/links/:linkId/revoke     (client OU cabinet)
//   GET  /api/firm/dashboard                (membre)
//   POST /api/firm/dsf-visa                 (session dossier, declarations:generate)
//
// Pièges : apiClient déballe déjà l'enveloppe { success, data } → les
// fonctions reçoivent directement `data`. Les dates arrivent en chaînes
// ISO. La bascule de dossier N'est PAS un hook react-query : elle est
// impérative (remplace le jeton + vide le cache) — voir TenantSwitcher.
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// Types — accès inter-dossiers
// ─────────────────────────────────────────────────────────────

/** Provenance d'un dossier accessible. */
export type TenantAccessVia = 'HOME' | 'FIRM' | 'DIRECT';

export interface AccessibleTenant {
  tenantId: string;
  name: string;
  slug: string;
  roleCode: string;
  via: TenantAccessVia;
}

/** Réponse de POST /auth/switch-tenant (nouveau jeton d'accès seul). */
export interface SwitchTenantResult {
  accessToken: string;
  expiresAt: string;
  tenant: {
    id: string;
    name: string;
    slug: string;
    businessType: string;
    ifu: string;
    isActive: boolean;
  };
  roleCode: string;
  permissions: string[];
  /** Firm the access is granted through; null for the home tenant and for a direct grant. */
  firm: { id: string; name: string } | null;
}

/**
 * Second factor sent with a switch into a client file: a verification
 * (or recovery) code, or a passkey assertion.
 */
export type SwitchStepUp =
  | { method: 'TOTP'; code: string }
  | { method: 'PASSKEY'; credential: unknown };

/** Factors the API accepts for the switch (`details.methods` of its 403). */
export type SecondFactorMethod = 'TOTP' | 'PASSKEY';

// ─────────────────────────────────────────────────────────────
// Types — cabinet & membres
// ─────────────────────────────────────────────────────────────

export type FirmRole = 'EXPERT' | 'COLLABORATEUR';

export interface Firm {
  id: string;
  name: string;
  ifu: string | null;
  email: string | null;
  phone: string | null;
  role: FirmRole;
  createdAt: string;
}

export interface FirmMember {
  id: string;
  userId: string;
  role: FirmRole;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface CreateFirmInput {
  name: string;
  ifu?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface InvitationResult {
  code: string;
  expiresAt: string;
  link: {
    id: string;
    firmId: string;
    tenantId: string;
    status: string;
    tenantName: string;
  };
}

// ─────────────────────────────────────────────────────────────
// Types — tableau de bord multi-dossiers
// ─────────────────────────────────────────────────────────────

export type LinkStatus = 'INVITED' | 'ACTIVE' | 'REVOKED';

export interface DossierSummary {
  tenant: { id: string; name: string; slug: string; ifu: string };
  linkId: string;
  /** Delegation scope the client granted; null for a role outside the four. */
  scope: FirmDelegationScope | null;
  /** Whether the requester holds this file (an EXPERT sees unstaffed files too). */
  hasAccess: boolean;
  accountingEnabled: boolean;
  exercice: { year: number; status: string | null };
  periodesNonVerrouillees: number;
  audcifCompliant: boolean;
  dsfGenerated: boolean;
  dsfVisa: { avis: string | null; generatedAt: string } | null;
  outboxFailed: number;
  derniereEcriture: {
    entryNumber: string;
    date: string;
    label: string;
    status: string;
  } | null;
}

export interface DashboardFirm {
  firmId: string;
  firmName: string;
  role: FirmRole;
  dossiers: DossierSummary[];
}

// ─────────────────────────────────────────────────────────────
// Types — visa DSF
// ─────────────────────────────────────────────────────────────

export type DsfVisaAvis = 'FAVORABLE' | 'RESERVES';

export interface CreateDsfVisaInput {
  exerciseYear: number;
  avis: DsfVisaAvis;
  commentaire?: string | null;
}

// ─────────────────────────────────────────────────────────────
// Types — côté client (cabinet lié à mon dossier)
// ─────────────────────────────────────────────────────────────

/** A firm member, and whether they hold one client file. */
export interface DossierTeamMember extends FirmMember {
  assigned: boolean;
  since: string | null;
}

/** A firm person who holds the client's file through a link. */
export interface FirmLinkPerson {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  since: string;
}

export interface TenantFirmLink {
  linkId: string;
  scope: FirmDelegationScope | null;
  people: FirmLinkPerson[];
  firmId: string;
  firmName: string;
  firmIfu: string | null;
  firmEmail: string | null;
  firmPhone: string | null;
  status: LinkStatus;
  activatedAt: string | null;
  createdAt: string;
}

export interface AcceptInvitationResult {
  firmId: string;
  tenantId: string;
  status: string;
  scope: FirmDelegationScope;
}

export interface ChangeLinkScopeResult {
  linkId: string;
  scope: FirmDelegationScope;
  changed: boolean;
}

// ─────────────────────────────────────────────────────────────
// Libellés français (affichage uniquement)
// ─────────────────────────────────────────────────────────────

export const FIRM_ROLE_LABELS: Record<string, string> = {
  EXPERT: 'Expert-comptable',
  COLLABORATEUR: 'Collaborateur',
};

export function firmRoleLabel(role: string): string {
  return FIRM_ROLE_LABELS[role] ?? role;
}

export const LINK_STATUS_LABELS: Record<string, string> = {
  INVITED: 'Invitation en attente',
  ACTIVE: 'Actif',
  REVOKED: 'Révoqué',
};

export function linkStatusLabel(status: string): string {
  return LINK_STATUS_LABELS[status] ?? status;
}

export const DSF_VISA_AVIS_LABELS: Record<string, string> = {
  FAVORABLE: 'Favorable',
  RESERVES: 'Avec réserves',
};

export function dsfVisaAvisLabel(avis: string): string {
  return DSF_VISA_AVIS_LABELS[avis] ?? avis;
}

export { DEFAULT_FIRM_DELEGATION_SCOPE, FIRM_DELEGATION_SCOPES };
export type { FirmDelegationScope };

export function delegationScopeLabel(scope: FirmDelegationScope | null): string {
  return scope ? FIRM_DELEGATION_SCOPE_LABELS[scope] : 'Périmètre non reconnu';
}

export function delegationScopeDescription(scope: FirmDelegationScope): string {
  return FIRM_DELEGATION_SCOPE_DESCRIPTIONS[scope];
}

/** Libellé de rôle de session (rôle mappé dans un dossier). */
export const ACCESS_ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrateur',
  ACCOUNTANT: 'Comptable',
  DELEGATION_CONSULTATION: 'Cabinet — Consultation',
  DELEGATION_TENUE: 'Cabinet — Tenue comptable',
  DELEGATION_TENUE_DECLARATIONS: 'Cabinet — Tenue et déclarations',
  MANAGER: 'Gestionnaire',
  OPERATOR: 'Opérateur',
  VERIFIER: 'Vérificateur',
  VIEWER: 'Lecture seule',
};

export function accessRoleLabel(roleCode: string): string {
  return ACCESS_ROLE_LABELS[roleCode] ?? roleCode;
}

// ─────────────────────────────────────────────────────────────
// Query keys
// ─────────────────────────────────────────────────────────────

export const firmKeys = {
  all: ['firm'] as const,
  accessibleTenants: () => [...firmKeys.all, 'accessible-tenants'] as const,
  firms: () => [...firmKeys.all, 'firms'] as const,
  members: (firmId: string) => [...firmKeys.all, 'members', firmId] as const,
  dashboard: () => [...firmKeys.all, 'dashboard'] as const,
  dossierTeam: (firmId: string, linkId: string) =>
    [...firmKeys.all, 'dossier-team', firmId, linkId] as const,
  tenantLinks: () => [...firmKeys.all, 'tenant-links'] as const,
};

// ─────────────────────────────────────────────────────────────
// Fonctions API
// ─────────────────────────────────────────────────────────────

async function fetchAccessibleTenants(): Promise<AccessibleTenant[]> {
  const result = await apiClient.get<{ tenants: AccessibleTenant[] }>(
    '/auth/accessible-tenants'
  );
  return result.tenants;
}

/**
 * Bascule vers un autre dossier. IMPÉRATIF (pas un hook) : renvoie le
 * nouveau jeton d'accès. L'appelant remplace la session (AuthContext
 * `applySwitchedSession`), vide le cache react-query et navigue vers « / ».
 */
export async function switchTenant(
  tenantId: string,
  stepUp?: SwitchStepUp
): Promise<SwitchTenantResult> {
  return apiClient.post<SwitchTenantResult>(
    '/auth/switch-tenant',
    stepUp ? { tenantId, stepUp } : { tenantId }
  );
}

/**
 * Passkey proof for the switch: requests a challenge bound to the signed-in
 * user, then has the browser sign it.
 */
export async function passkeyStepUp(): Promise<SwitchStepUp> {
  const options = await apiClient.post<Parameters<typeof startAuthentication>[0]['optionsJSON']>(
    '/auth/passkey/step-up/options',
    {}
  );
  const credential = await startAuthentication({ optionsJSON: options });
  return { method: 'PASSKEY', credential };
}

async function fetchFirms(): Promise<Firm[]> {
  const result = await apiClient.get<{ firms: Firm[] }>('/firm/firms');
  return result.firms;
}

async function createFirm(input: CreateFirmInput): Promise<Firm> {
  const result = await apiClient.post<{ firm: Firm }>('/firm/firms', input);
  return result.firm;
}

async function fetchMembers(firmId: string): Promise<FirmMember[]> {
  const result = await apiClient.get<{ members: FirmMember[] }>(
    `/firm/firms/${firmId}/members`
  );
  return result.members;
}

async function addMember(
  firmId: string,
  email: string,
  role: FirmRole
): Promise<FirmMember> {
  const result = await apiClient.post<{ member: FirmMember }>(
    `/firm/firms/${firmId}/members`,
    { email, role }
  );
  return result.member;
}

async function removeMember(firmId: string, userId: string): Promise<void> {
  await apiClient.delete<{ removed: boolean }>(
    `/firm/firms/${firmId}/members/${userId}`
  );
}

async function createInvitation(
  firmId: string,
  ifu: string
): Promise<InvitationResult> {
  // The API keeps no idempotent copy of the code, so a queued replay would mint a second one.
  return apiClient.post<InvitationResult>(
    `/firm/firms/${firmId}/invitations`,
    { ifu },
    { noQueue: true }
  );
}

async function fetchDashboard(): Promise<DashboardFirm[]> {
  const result = await apiClient.get<{ firms: DashboardFirm[] }>('/firm/dashboard');
  return result.firms;
}

async function createDsfVisa(input: CreateDsfVisaInput): Promise<{ id: string }> {
  const result = await apiClient.post<{ visa: { id: string } }>(
    '/firm/dsf-visa',
    input
  );
  return result.visa;
}

async function fetchTenantLinks(): Promise<TenantFirmLink[]> {
  const result = await apiClient.get<{ links: TenantFirmLink[] }>('/firm/links');
  return result.links;
}

async function acceptInvitation(
  code: string,
  scope: FirmDelegationScope
): Promise<AcceptInvitationResult> {
  return apiClient.post<AcceptInvitationResult>('/firm/links/accept', { code, scope });
}

async function changeLinkScope(
  linkId: string,
  scope: FirmDelegationScope
): Promise<ChangeLinkScopeResult> {
  return apiClient.put<ChangeLinkScopeResult>(`/firm/links/${linkId}/scope`, { scope });
}

async function fetchDossierTeam(firmId: string, linkId: string): Promise<DossierTeamMember[]> {
  const result = await apiClient.get<{ team: DossierTeamMember[] }>(
    `/firm/firms/${firmId}/links/${linkId}/team`
  );
  return result.team;
}

async function assignDossierMember(firmId: string, linkId: string, userId: string) {
  return apiClient.post<{ linkId: string; userId: string; assigned: boolean }>(
    `/firm/firms/${firmId}/links/${linkId}/team`,
    { userId }
  );
}

async function unassignDossierMember(firmId: string, linkId: string, userId: string) {
  return apiClient.delete<{ linkId: string; userId: string; assigned: boolean }>(
    `/firm/firms/${firmId}/links/${linkId}/team/${userId}`
  );
}

async function revokeLink(linkId: string): Promise<{ linkId: string; status: string }> {
  return apiClient.post<{ linkId: string; status: string }>(
    `/firm/links/${linkId}/revoke`,
    {}
  );
}

// ─────────────────────────────────────────────────────────────
// Hooks — accès inter-dossiers
// ─────────────────────────────────────────────────────────────

/**
 * Dossiers ouvrables par l'utilisateur (tenant d'origine + accès cabinet).
 * Sert au sélecteur de dossier (TenantSwitcher) : le menu n'apparaît que
 * s'il y a plus d'un dossier.
 */
export function useAccessibleTenants(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: firmKeys.accessibleTenants(),
    queryFn: fetchAccessibleTenants,
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Bascule de session vers un dossier, prête à l'emploi (TenantSwitcher,
 * bouton « Ouvrir » du tableau de bord). Effectue : POST /auth/switch-tenant
 * → remplace la session EN MÉMOIRE (jamais de rechargement dur) → vide TOUT
 * le cache react-query → navigue vers « / ». Lève en cas d'échec :
 * l'appelant gère son état de chargement et le toast.
 *
 * `keepPage` reopens the file the screens already show (after a refresh
 * brought the token home): the token is replaced, the cache and the page
 * are kept.
 */
export function useSwitchToTenant() {
  const { tenant, applySwitchedSession } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useCallback(
    async (tenantId: string, stepUp?: SwitchStepUp, options?: { keepPage?: boolean }) => {
      const keepPage = options?.keepPage === true;
      if (!keepPage && tenantId === tenant?.id) {
        navigate('/');
        return;
      }
      const result = await switchTenant(tenantId, stepUp);
      applySwitchedSession({
        accessToken: result.accessToken,
        expiresAt: result.expiresAt,
        tenant: {
          id: result.tenant.id,
          name: result.tenant.name,
          slug: result.tenant.slug,
        },
        permissions: result.permissions,
        firm: result.firm ?? null,
      });
      if (keepPage) return;
      queryClient.clear();
      navigate('/');
    },
    [tenant?.id, applySwitchedSession, queryClient, navigate]
  );
}

// ─────────────────────────────────────────────────────────────
// Hooks — cabinet & membres
// ─────────────────────────────────────────────────────────────

/** Cabinets dont l'utilisateur est membre (avec son rôle). */
export function useFirms(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: firmKeys.firms(),
    queryFn: fetchFirms,
    enabled: options?.enabled ?? true,
  });
}

/** Membres d'un cabinet (demandeur = membre). */
export function useFirmMembers(firmId: string | undefined) {
  return useQuery({
    queryKey: firmKeys.members(firmId ?? ''),
    queryFn: () => fetchMembers(firmId!),
    enabled: !!firmId,
  });
}

/** Crée un cabinet (le créateur devient EXPERT). */
export function useCreateFirm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFirmInput) => createFirm(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: firmKeys.firms() });
      queryClient.invalidateQueries({ queryKey: firmKeys.accessibleTenants() });
    },
  });
}

/** Ajoute un membre (EXPERT). */
export function useAddMember(firmId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: FirmRole }) =>
      addMember(firmId, email, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: firmKeys.members(firmId) });
    },
  });
}

/** Retire un membre (EXPERT). */
export function useRemoveMember(firmId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeMember(firmId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: firmKeys.members(firmId) });
    },
  });
}

/** Génère un code d'invitation pour un dossier client (par IFU). */
export function useCreateInvitation(firmId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ifu: string) => createInvitation(firmId, ifu),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: firmKeys.dashboard() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Hooks — tableau de bord & visa DSF
// ─────────────────────────────────────────────────────────────

/** Tableau de bord multi-dossiers (dossiers ACTIFS des cabinets du membre). */
export function useFirmDashboard(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: firmKeys.dashboard(),
    queryFn: fetchDashboard,
    staleTime: 1000 * 30,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Enregistre un visa DSF sur le dossier de la SESSION COURANTE. Le serveur
 * exige un mandat cabinet ACTIF sur ce dossier : la bascule dans le dossier
 * (switch-tenant) est un préalable (403 sinon).
 */
export function useCreateDsfVisa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDsfVisaInput) => createDsfVisa(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: firmKeys.dashboard() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Hooks — côté client (cabinet lié à mon dossier)
// ─────────────────────────────────────────────────────────────

/** Cabinets liés au dossier courant (client, tenant:settings). */
export function useTenantFirmLinks(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: firmKeys.tenantLinks(),
    queryFn: fetchTenantLinks,
    enabled: options?.enabled ?? true,
  });
}

/** Accepte un code d'invitation avec le périmètre choisi (client admin, tenant:settings). */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, scope }: { code: string; scope: FirmDelegationScope }) =>
      acceptInvitation(code, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: firmKeys.tenantLinks() });
    },
  });
}

/** Change le périmètre de délégation d'une liaison active (client admin). */
export function useChangeLinkScope() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, scope }: { linkId: string; scope: FirmDelegationScope }) =>
      changeLinkScope(linkId, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: firmKeys.tenantLinks() });
    },
  });
}

/** Équipe d'un dossier client : membres du cabinet et affectation (EXPERT). */
export function useDossierTeam(firmId: string, linkId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: firmKeys.dossierTeam(firmId, linkId),
    queryFn: () => fetchDossierTeam(firmId, linkId),
    enabled: options?.enabled ?? true,
  });
}

/** Affecte ou retire un membre du cabinet d'un dossier client (EXPERT). */
export function useSetDossierAssignment(firmId: string, linkId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, assigned }: { userId: string; assigned: boolean }) =>
      assigned
        ? assignDossierMember(firmId, linkId, userId)
        : unassignDossierMember(firmId, linkId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: firmKeys.dossierTeam(firmId, linkId) });
      queryClient.invalidateQueries({ queryKey: firmKeys.dashboard() });
    },
  });
}

/** Révoque une liaison cabinet (client OU cabinet). */
export function useRevokeLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => revokeLink(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: firmKeys.tenantLinks() });
      queryClient.invalidateQueries({ queryKey: firmKeys.dashboard() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Exports utilitaires
// ─────────────────────────────────────────────────────────────

/** Type mince partagé pour l'affichage d'un dossier actif (header). */
export type ActiveTenant = Pick<Tenant, 'id' | 'name' | 'slug'>;

export {
  fetchAccessibleTenants,
  fetchFirms,
  createFirm,
  fetchMembers,
  addMember,
  removeMember,
  createInvitation,
  fetchDashboard,
  createDsfVisa,
  fetchTenantLinks,
  acceptInvitation,
  changeLinkScope,
  fetchDossierTeam,
  assignDossierMember,
  unassignDossierMember,
  revokeLink,
};
